import { useEffect, useState } from "react";
import { deserializeEditGraph, resolveEdit, serializeEditGraph } from "@desk/core";
import { createGraphForPack, PACK_REGISTRY } from "../content/registry.js";
import {
  crowdReaction,
  clamp01,
  EMOTION_AXES,
  EMOTION_AXIS_COLORS,
  EMOTION_TUNING,
  type EmotionAxis,
  type EmotionVector,
  type CrowdEmotionProfile,
} from "../game/emotion-model.js";
import {
  computeAudienceResult,
  landingContextFromPresentation,
  landingEmotionVector,
} from "../game/audience-objective.js";
import { landingConfigForPack } from "../content/landing/index.js";
import { CONSTRAINT_LABELS } from "../game/challenge/constraints.js";
import { resolveResultCardTheme } from "../game/store-catalog.js";
import type { GameController } from "../shell/hooks/useGameController.js";
import type { AudienceResult } from "../game/types.js";
import { ArtifactPreview } from "../shell/ui/ArtifactPreview.js";
import { CompareDetailsDrawer } from "../shell/ui/CompareDetailsDrawer.js";
import { FlowChrome } from "../shell/ui/FlowChrome.js";
import { FlowEmpty } from "../shell/ui/FlowStates.js";
import { ConstraintChips } from "../shell/ui/FlowStates.js";
import { MetricPillGrid } from "../shell/ui/MetricPillGrid.js";
import { ReconstructionLabel } from "../shell/ui/ReconstructionLabel.js";
import { ShareChallengeButton } from "../shell/ui/ShareChallengeButton.js";
import { SourceBeatStrip } from "../shell/ui/SourceBeatStrip.js";
import { publishResultIdentity, trackLevelResultOnce } from "../game/analytics.js";

function prefersReducedMotion(): boolean {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

export function CompareScreen({ game }: { game: GameController }) {
  const { state, dispatch } = game;
  const snap = state.lastPublish;
  const challenge = state.challenge;
  const isChallengeCompare = Boolean(challenge?.playerPublish && challenge.publishRevealed);

  // Merged results reveal (Job 6): the reach headline counts up on the single
  // post-publish screen — a tasteful reveal that folds the old Publish step in.
  const [shownReach, setShownReach] = useState(0);
  const [reachAnnounce, setReachAnnounce] = useState("");
  const reachTarget = snap?.rewards.reachGain ?? 0;
  const testSpeed = state.settings.testSpeed;
  useEffect(() => {
    if (!snap) return;
    const reduce = prefersReducedMotion();
    if (testSpeed || reduce) {
      setShownReach(reachTarget);
      setReachAnnounce(`Reach ${reachTarget}`);
      return;
    }
    setShownReach(0);
    const start = performance.now();
    const dur = 1600;
    let raf = 0;
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / dur);
      const eased = 1 - (1 - t) * (1 - t);
      setShownReach(Math.round(reachTarget * eased));
      if (t < 1) raf = requestAnimationFrame(tick);
      else setReachAnnounce(`Reach ${reachTarget}`);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [snap, reachTarget, testSpeed]);

  // Anonymous analytics: one `level_result` event per crowd, fired when this
  // publish's per-audience result is computed (the same `computeAudienceResult`
  // read the outcome block below renders). Challenge compares don't score a
  // campaign audience result the same way, so they're skipped here. Presentation
  // only — recomputing for tracking never re-scores or touches the game state.
  //
  // Deduped by publish identity (levelId + a hash of the published graph) via
  // `trackLevelResultOnce`: an autosave reload/resume can restore straight
  // onto this screen with the SAME `lastPublish` and remount CompareScreen —
  // without the dedupe, that would re-fire the identical level_result batch
  // and double-count the "which levels land well/badly" metric. A new publish
  // (different identity) still reports normally.
  useEffect(() => {
    if (!snap || isChallengeCompare) return;
    try {
      const entry = PACK_REGISTRY[snap.packId];
      const landingConfig = landingConfigForPack(snap.packId);
      const chosenCrowdId = state.currentEdit?.chosenCrowdId ?? landingConfig.servedCrowdId;
      const resolved = resolveEdit(deserializeEditGraph(snap.graphJson), entry.pack);
      const landing = landingContextFromPresentation(entry.pack, snap.presentation, {
        seamlessTransitions: state.purchases.includes("seamless-transitions"),
      });
      const result = computeAudienceResult(resolved, landingConfig, chosenCrowdId, snap.score, landing);
      trackLevelResultOnce(publishResultIdentity(snap.packId, snap.graphJson), {
        levelId: snap.packId,
        crowdId: result.crowdId,
        landed: result.landed,
      });
    } catch {
      /* presentation-only recompute for analytics; never throw into the game */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snap?.packId, snap?.graphJson, isChallengeCompare]);

  if (!snap) {
    return (
      <FlowChrome testId="screen-compare" stage="Compare" title="Same source">
        <FlowEmpty message="No comparison ready." />
      </FlowChrome>
    );
  }
  const c = snap.compare;
  const score = snap.score;
  const entry = PACK_REGISTRY[snap.packId];
  const sourceGraphJson = serializeEditGraph(createGraphForPack(snap.packId, snap.seed));

  const challengerScore = isChallengeCompare ? challenge?.challengerScore ?? null : null;
  const challengerReach = challengerScore?.reach;

  const metricPills = [
    {
      id: "cred",
      label: "Cred",
      value: score.credDelta,
      definition: "Delayed reputation from what the edit kept or lost.",
      testId: "cred-delta",
      vs: challengerScore ? `Challenger ${challengerScore.credDelta}` : undefined,
      vsTestId: challengerScore ? "vs-challenger-cred" : undefined,
    },
    {
      id: "context",
      label: "Context loss",
      value: score.contextLoss,
      definition: "Key beats left out or cut short.",
      testId: "context-loss",
      vs: challengerScore ? `Challenger ${challengerScore.contextLoss}` : undefined,
      vsTestId: challengerScore ? "vs-challenger-context-loss" : undefined,
    },
    {
      id: "claim",
      label: "Claim drift",
      value: score.claimDrift,
      definition: "Headline claims the kept beats do not support.",
      testId: "claim-drift",
      vs: challengerScore ? `Challenger ${challengerScore.claimDrift}` : undefined,
      vsTestId: challengerScore ? "vs-challenger-claim-drift" : undefined,
    },
    {
      id: "provenance",
      label: "Provenance",
      value: score.provenanceRisk,
      definition: "How far casting drifted from the source subject.",
      testId: "provenance-risk",
      vs: challengerScore ? `Challenger ${challengerScore.provenanceRisk}` : undefined,
      vsTestId: challengerScore ? "vs-challenger-provenance-risk" : undefined,
    },
  ];

  if (!isChallengeCompare) {
    // Reward-delta display, folded in from the old Publish screen (Job 6). Shows
    // what THIS publish actually paid and celebrates a new best. Falls back to the
    // frozen score-based reward for old snapshots that predate `earned`.
    const earned = snap.earned;
    const awardFollowers = earned ? earned.followers : snap.rewards.followersGain;
    const awardCash = earned ? earned.cash : snap.rewards.cashGain;
    const rewardNote: { kind: string; text: string } | null = (() => {
      if (!earned) return null;
      if (earned.cleanSweep)
        return { kind: "sweep", text: "Clean sweep! All crowds landed. Bonus followers + Bits." };
      if (!earned.landed)
        return { kind: "flop", text: "Didn't land this crowd yet. Re-cut to cross the line." };
      if (earned.firstLanding) return { kind: "first", text: "Landed. New best for this crowd!" };
      if (earned.improved) return { kind: "improved", text: "New best! You beat your last cut." };
      return { kind: "held", text: "Your best still stands. No new payout this time." };
    })();

    const bars = [
      { id: "context-loss", label: "Context loss", value: score.contextLoss, color: "var(--pink)" },
      { id: "claim-drift", label: "Claim drift", value: score.claimDrift, color: "var(--amber)" },
      { id: "provenance-risk", label: "Provenance risk", value: score.provenanceRisk, color: "var(--warn)" },
    ];
    const yourLine = `Your ${entry?.title ?? "reel"} cut`;

    // ── Per-audience OUTCOME read (presentation only) ───────────────────────
    // Which crowd the player cut for, whether the edit LANDED them, and the full
    // landing VECTOR + reaction so the results block can teach a clear
    // "needed → delivered → fit → cost" read. Recomputed from the frozen
    // snapshot; never re-scores. `landingConfig` also drives the 3/3 tally.
    const landingConfig = landingConfigForPack(snap.packId);
    const outcome = (() => {
      const chosenCrowdId = state.currentEdit?.chosenCrowdId ?? landingConfig.servedCrowdId;
      try {
        const resolved = resolveEdit(deserializeEditGraph(snap.graphJson), entry.pack);
        // Reconstruct the same landing inputs publish used (effects / caption
        // placement / trims / jump-cuts) so Compare's read matches the result the
        // player earned — preview == publish == compare.
        const landing = landingContextFromPresentation(entry.pack, snap.presentation, {
          seamlessTransitions: state.purchases.includes("seamless-transitions"),
        });
        const result = computeAudienceResult(resolved, landingConfig, chosenCrowdId, snap.score, landing);
        const profile = landingConfig.crowds[result.crowdId] ?? landingConfig.crowds[landingConfig.servedCrowdId]!;
        const vector = landingEmotionVector(resolved, landingConfig, landing);
        const reaction = crowdReaction(vector, profile, result.crowdId === landingConfig.servedCrowdId);
        return { result, profile, vector, reaction, label: profile.label };
      } catch {
        return null;
      }
    })();

    // How many of this story's crowds have now landed (banked best + this cut),
    // so a completed level (3/3) can earn the top-tier verdict.
    const landedCrowdCount = (() => {
      const prog = state.audienceProgress?.[snap.packId] ?? {};
      const ids = new Set<string>();
      for (const [cid, r] of Object.entries(prog)) if (r?.landed) ids.add(cid);
      if (outcome?.result.landed) ids.add(outcome.result.crowdId);
      return ids.size;
    })();

    // ── Verdict tier + animated headline (deterministic — seeded from the
    // publish snapshot's graph + seq/seed, NO Math.random / Date.now) ────────
    const outFit = outcome ? (outcome.result.fitMilli ?? 0) / 1000 : 0;
    const outLanded = outcome?.result.landed ?? false;
    const outClean = outcome?.result.clean ?? false;
    const is3of3 = landedCrowdCount >= landingConfig.crowdOrder.length;
    const verdictTier: VerdictTier = outLanded
      ? outClean || outFit >= 0.8 || is3of3
        ? "nailed"
        : "hit"
      : outFit >= 0.45
        ? "close"
        : "flop";
    const verdictWord = pickVerdict(verdictTier, snap.graphJson, snap.seed, state.seq);
    const verdictColor =
      verdictTier === "nailed" || verdictTier === "hit" ? "var(--teal)" : "var(--amber)";

    return (
      <div className="desk-app desk-chrome compare-screen compare-mock" data-testid="screen-compare">
        <div className="compare-inner">
          <ReconstructionLabel show={c.showsReconstructionLabel} />

          <div className="publish-live-tag">◆ IT&apos;S LIVE ◆</div>
          <div className="publish-reach-hero" data-testid="reach-countup">
            <span className="sr-only">Reach</span>
            <strong className="publish-reach-num">{fmtCount(shownReach)}</strong>
          </div>
          <div className="publish-reach-sub">views in the first hour</div>
          <span className="sr-only" aria-live="polite">
            {reachAnnounce}
          </span>

          <div className="publish-stat-row" data-testid="publish-rewards-early">
            <div className="publish-stat">
              <span className="publish-stat-label">FOLLOWERS</span>
              <span className="publish-stat-val publish-stat-teal" data-testid="reward-followers">
                +{awardFollowers}
              </span>
            </div>
            <div className="publish-stat">
              <span className="publish-stat-label">BITS</span>
              <span className="publish-stat-val" data-testid="reward-bits">
                +{awardCash} Bits
              </span>
            </div>
          </div>
          {rewardNote ? (
            <p
              className="publish-reward-note"
              data-testid="reward-note"
              data-kind={rewardNote.kind}
            >
              {rewardNote.text}
            </p>
          ) : null}

          {/* Animated verdict — punchy, deterministic per publish snapshot. */}
          <div
            className={`compare-verdict compare-verdict-${verdictTier}`}
            data-testid="results-verdict"
            data-tier={verdictTier}
          >
            <span className="compare-verdict-word" style={{ color: verdictColor }}>
              {verdictWord}
            </span>
          </div>
          <div className="compare-your-cut" data-testid="your-reel-artifact">
            {yourLine}
          </div>

          {/* Focused outcome: what the crowd NEEDED → what you DELIVERED → FIT → COST. */}
          {outcome ? (
            <OutcomeBlock
              label={outcome.label}
              result={outcome.result}
              profile={outcome.profile}
              vector={outcome.vector}
              credDelta={score.credDelta}
            />
          ) : null}

          {/* Craft breakdown — de-emphasized, optional, collapsed. Not the point
              of this screen, but kept for the curious (and for teaching). */}
          <details className="compare-craft" data-testid="compare-craft">
            <summary className="compare-craft-summary">Craft breakdown</summary>
            <div className="compare-metrics">
              {bars.map((b) => (
                <div className="compare-metric" data-testid={b.id} key={b.id}>
                  <div className="compare-metric-row">
                    <span className="compare-metric-label">{b.label}</span>
                    <span className="compare-metric-val" style={{ color: b.color }}>
                      {Math.max(0, Math.round(b.value))}%
                    </span>
                  </div>
                  <div className="compare-metric-track">
                    <div
                      className="compare-metric-fill"
                      style={{
                        width: `${Math.min(100, Math.max(0, Math.round(b.value)))}%`,
                        background: b.color,
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </details>

          <div className="compare-hidden-extras" aria-hidden>
            <div className="cred-reveal" data-testid="cred-reveal">
              <MetricPillGrid pills={[metricPills[0]!]} testId="metric-pill-grid" />
            </div>
            <p className="hint-line compare-beat-line" data-testid="compare-beat-summary">
              Kept {c.retainedBeats.length}
              {c.trimmedBeats.length ? ` · trimmed ${c.trimmedBeats.length}` : ""}
              {c.omittedBeats.length ? ` · omitted ${c.omittedBeats.length}` : ""}
              {c.reordered ? " · order changed" : ""}
            </p>
            <CompareDetailsDrawer compare={c} />
          </div>

          <div className="publish-comments-head">TOP COMMENTS</div>
          <ul className="reaction-list publish-comments" data-testid="reaction-list">
            {snap.comments.slice(0, 3).map((cmt) => (
              <li key={cmt.id} data-tone={cmt.tone} className="publish-comment">
                <span className="publish-comment-handle">{cmt.handle}</span>
                <span className="publish-comment-text">{cmt.body}</span>
              </li>
            ))}
          </ul>

          <div className="compare-actions">
            <button
              type="button"
              className="compare-store-btn"
              data-testid="compare-roadmap"
              onClick={() => dispatch({ type: "OPEN_ROADMAP" })}
            >
              Your desk
            </button>
            {outcome && !outcome.result.landed ? (
              // Flop (Job 5a): the chosen crowd did NOT land — the primary action
              // re-opens the editor for the SAME story + crowd with the player's
              // edit loaded so they can refine and republish. No auto-advance.
              <button
                type="button"
                className="primary compare-next-btn"
                data-testid="results-retry"
                onClick={() => dispatch({ type: "ENTER_EDITOR" })}
              >
                Retry
              </button>
            ) : (
              <button
                type="button"
                className="primary compare-next-btn"
                data-testid="results-continue"
                onClick={() => dispatch({ type: "CONTINUE_AFTER_RESULTS" })}
              >
                Next story
              </button>
            )}
          </div>
          {/* Share your reel — a visible SECONDARY action (native share sheet →
              WhatsApp/Messages, or copy link). Friends open the link and try to
              beat your edit. Was previously hidden in `.compare-hidden-extras`. */}
          <ShareChallengeButton
            className="share-challenge compare-share"
            packId={snap.packId}
            packVersion={snap.packVersion}
            seed={snap.seed}
            graphJson={snap.graphJson}
            score={snap.score}
            constraintIds={challenge?.constraintIds ?? []}
            presentation={snap.presentation}
            label="Share your reel"
          />
        </div>
      </div>
    );
  }

  return (
    <FlowChrome
      testId="screen-compare"
      className="compare-screen"
      stage={isChallengeCompare ? "Challenge" : "Compare"}
      title={isChallengeCompare ? "Challenger vs You" : "Edited vs Source"}
      hook="Cred shows up here, not while editing."
      actions={
        <>
          {isChallengeCompare ? (
            <button
              type="button"
              data-testid="save-challenge-portfolio"
              onClick={() => dispatch({ type: "SAVE_CHALLENGE_TO_PORTFOLIO" })}
            >
              Save to Portfolio
            </button>
          ) : null}
          <button
            type="button"
            className="primary"
            data-testid={isChallengeCompare ? "challenge-done" : "results-continue"}
            onClick={() => dispatch({ type: "CONTINUE_AFTER_RESULTS" })}
          >
            {isChallengeCompare ? "Done" : "Continue"}
          </button>
        </>
      }
    >
      <ReconstructionLabel show={c.showsReconstructionLabel} />

      {isChallengeCompare && challenge ? (
        <div className="compare-artifacts challenge-dual" data-testid="challenge-artifact-pair">
          <ArtifactPreview
            packId={challenge.packId}
            pack={PACK_REGISTRY[challenge.packId].pack}
            graphJson={challenge.challengerGraphJson}
            label="Challenger"
            scoreText={`Reach ${challengerReach}`}
            testId="challenger-artifact"
            compact
          />
          <ArtifactPreview
            packId={snap.packId}
            pack={entry.pack}
            graphJson={snap.graphJson}
            label="You"
            scoreText={`Reach ${score.reach}`}
            testId="you-artifact"
            compact
          />
        </div>
      ) : (
        <div className="compare-artifacts compare-vs-layout" data-testid="compare-artifacts">
          <div className="compare-pane">
            <p className="compare-pane-label">Edited</p>
            <ArtifactPreview
              packId={snap.packId}
              pack={entry.pack}
              graphJson={snap.graphJson}
              label="Your reel"
              scoreText={`Reach ${score.reach}`}
              resultCardTheme={resolveResultCardTheme({
                purchases: state.purchases,
                equippedTheme: state.equippedTheme,
              })}
              testId="your-reel-artifact"
            />
          </div>
          <div className="source-record-panel compare-pane" data-testid="full-source-record">
            <p className="compare-pane-label">Source</p>
            <SourceBeatStrip pack={entry.pack} compare={c} />
            <ArtifactPreview
              packId={snap.packId}
              pack={entry.pack}
              graphJson={sourceGraphJson}
              label="Canonical source"
              scoreText="Source"
              testId="source-artifact"
              compact
            />
          </div>
        </div>
      )}

      {isChallengeCompare && challengerScore && (
        <div
          className="challenge-vs"
          data-testid="challenge-score-vector"
          data-challenger-reach={challengerScore.reach}
          data-you-reach={score.reach}
          data-challenger-cred={challengerScore.credDelta}
          data-you-cred={score.credDelta}
          data-challenger-context={challengerScore.contextLoss}
          data-you-context={score.contextLoss}
          data-challenger-claim={challengerScore.claimDrift}
          data-you-claim={score.claimDrift}
          data-challenger-provenance={challengerScore.provenanceRisk}
          data-you-provenance={score.provenanceRisk}
        >
          <div>
            <span className="score-label">Challenger Reach</span>
            <strong data-testid="vs-challenger-reach" data-score-dim="reach">
              {challengerReach}
            </strong>
          </div>
          <div>
            <span className="score-label">Your Reach</span>
            <strong data-testid="vs-you-reach" data-score-dim="reach">
              {score.reach}
            </strong>
          </div>
        </div>
      )}

      <div className="cred-reveal" data-testid="cred-reveal">
        <MetricPillGrid
          pills={metricPills}
          testId={isChallengeCompare ? "challenge-metric-grid" : "metric-pill-grid"}
        />
      </div>

      <p className="hint-line compare-beat-line" data-testid="compare-beat-summary">
        Kept {c.retainedBeats.length}
        {c.trimmedBeats.length ? ` · trimmed ${c.trimmedBeats.length}` : ""}
        {c.omittedBeats.length ? ` · omitted ${c.omittedBeats.length}` : ""}
        {c.reordered ? " · order changed" : ""}
      </p>

      {isChallengeCompare && challenge && challenge.constraintIds.length > 0 && (
        <section className="compare-constraints-block">
          <ConstraintChips
            labels={challenge.constraintIds.map((id) => CONSTRAINT_LABELS[id])}
            testId="compare-constraints"
          />
          {challenge.deadlineBonusAwarded && (
            <p className="hint-line" data-testid="deadline-bonus-note">
              Soft deadline bonus for this challenge only.
            </p>
          )}
        </section>
      )}

      <CompareDetailsDrawer compare={c} />

      <ShareChallengeButton
        packId={snap.packId}
        packVersion={snap.packVersion}
        seed={snap.seed}
        graphJson={snap.graphJson}
        score={snap.score}
        constraintIds={challenge?.constraintIds ?? []}
        presentation={snap.presentation}
        label="Share challenge"
      />
    </FlowChrome>
  );
}

function fmtCount(n: number): string {
  if (n >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(n >= 1e5 ? 0 : 1)}K`;
  return `${Math.round(n)}`;
}

// ── Verdict headline (deterministic, per-tier pools) ──────────────────────────
type VerdictTier = "flop" | "close" | "hit" | "nailed";

/** Per-tier verdict pools — many strings so the reveal feels alive. */
const VERDICTS: Record<VerdictTier, readonly string[]> = {
  flop: ["FLOPPED", "CRICKETS", "NOBODY BIT", "SWIPED PAST", "TUMBLEWEEDS", "SCROLLED PAST", "DEAD AIR", "IGNORED"],
  close: ["SO CLOSE", "ALMOST", "ONE MORE CUT", "NEARLY", "RIGHT THERE", "INCHES OFF", "ALMOST HAD IT"],
  hit: ["HIT", "IT LANDED", "THEY BIT", "LANDED", "NICE HIT", "STUCK IT", "IN THE GREEN"],
  nailed: ["NAILED IT", "AMAZING", "VIRAL", "CHEF'S KISS", "FLAWLESS", "ICONIC", "THEY ATE IT UP"],
};

/** Stable FNV-1a hash of a string (deterministic; no Math.random / Date.now). */
function hashString(s: string): number {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/**
 * Deterministic verdict pick, seeded from the FROZEN publish snapshot (graph +
 * seed + publish seq). A given publish always reads the same word; different
 * publishes vary. Never uses Math.random / Date.now.
 */
function pickVerdict(tier: VerdictTier, graphJson: string, seed: number, seq: number): string {
  const pool = VERDICTS[tier];
  const h = (hashString(graphJson) ^ (seed >>> 0) ^ Math.imul(seq >>> 0, 2654435761)) >>> 0;
  return pool[h % pool.length]!;
}

/**
 * The focused OUTCOME block: for the crowd the player cut for, a teaching read of
 * what they NEEDED (green wants), what the edit DELIVERED (which wants it hit),
 * the FIT %, and what it COST (clean vs receipts / cred). Derived from the crowd's
 * reaction + the landing vector + the published credDelta. Presentation only.
 */
function OutcomeBlock({
  label,
  result,
  profile,
  vector,
  credDelta,
}: {
  label: string;
  result: AudienceResult;
  profile: CrowdEmotionProfile;
  vector: EmotionVector;
  credDelta: number;
}) {
  const wantAxes = EMOTION_AXES.filter((a) => profile.green[a] > 0);
  const hit = (a: EmotionAxis) =>
    clamp01(vector[a]) >= EMOTION_TUNING.GREEN_HIT_FRAC * profile.green[a];
  const fitPct = Math.round((result.fitMilli ?? 0) / 10);
  const doubtPct = Math.round((result.doubtMilli ?? 0) / 10);
  const landed = result.landed;
  const clean = result.clean;
  const statusText = landed
    ? clean
      ? "landed clean, no receipts"
      : "landed, left receipts"
    : `didn't land · ${result.status}`;
  const costText = clean
    ? "Clean. Nobody can check it."
    : `Receipts left: Doubt ${doubtPct}%${credDelta < 0 ? ` · cred ${credDelta}` : ""}`;
  return (
    <div
      className={`compare-audience compare-outcome${landed ? " is-landed" : " is-flop"}${
        clean ? " is-clean" : ""
      }`}
      data-testid="compare-audience"
      data-landed={landed ? "1" : "0"}
      data-clean={clean ? "1" : "0"}
    >
      <div className="compare-outcome-head">
        <span className="compare-outcome-face" aria-hidden>
          {result.face}
        </span>
        <strong className="compare-outcome-crowd">{label}</strong>
        <span className="compare-outcome-status">{statusText}</span>
      </div>
      <div className="compare-outcome-grid">
        <div className="compare-outcome-row">
          <span className="compare-outcome-key">NEEDED</span>
          <span className="compare-outcome-axes">
            {wantAxes.map((a) => (
              <span key={a} className="compare-axis-chip" style={{ color: EMOTION_AXIS_COLORS[a] }}>
                {a}
              </span>
            ))}
          </span>
        </div>
        <div className="compare-outcome-row">
          <span className="compare-outcome-key">DELIVERED</span>
          <span className="compare-outcome-axes">
            {wantAxes.map((a) => (
              <span
                key={a}
                className={`compare-axis-chip${hit(a) ? " is-hit" : " is-miss"}`}
                style={hit(a) ? { color: EMOTION_AXIS_COLORS[a], borderColor: EMOTION_AXIS_COLORS[a] } : undefined}
              >
                {hit(a) ? "✓" : "○"} {a}
              </span>
            ))}
          </span>
        </div>
        <div className="compare-outcome-row">
          <span className="compare-outcome-key">FIT</span>
          <span className="compare-outcome-fit" data-testid="compare-outcome-fit">
            {fitPct}%
          </span>
        </div>
        <div className="compare-outcome-row">
          <span className="compare-outcome-key">COST</span>
          <span className={`compare-outcome-cost${clean ? " is-clean" : " is-dirty"}`}>{costText}</span>
        </div>
      </div>
    </div>
  );
}
